import { api } from "./api";
import {
  TAIResponse,
  TRequestAI,
  ChatMessage,
  ConversationTab,
  DisplayMessage,
  TQuestionAudioRequestDto,
  TQuestionAudioUrlResponseDto,
} from "../types/ai.types";

export type { ChatMessage, ConversationTab };

export const aiApi = api.injectEndpoints({
  endpoints: (build) => ({
    getResponseFromAI: build.mutation<
      TAIResponse,
      TRequestAI & { conversationId?: number | null }
    >({
      query: ({ conversationId, ...body }) => ({
        url: `ai/AI_GetResponseFromAI/${conversationId || ""}/1/1`,
        method: "POST",
        body: body,
      }),

      async onQueryStarted(
        { conversationId, message },
        { dispatch, queryFulfilled }
      ) {
        if (!message) {
          return;
        }
        if (!conversationId) {
          try {
            await queryFulfilled;
            dispatch(aiApi.util.invalidateTags(["Conversations"]));
          } catch {}
          return;
        }

        const patchResult = dispatch(
          aiApi.util.updateQueryData(
            "getConversationHistory",
            conversationId,
            (draft: DisplayMessage[]) => {
              const userMessage: DisplayMessage = {
                role: "user",
                content: message,
                date: new Date().toISOString(),
                time: "",
              };
              draft.push(userMessage);
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error, { conversationId }) =>
        conversationId
          ? [
              { type: "ConversationHistory", id: conversationId },
              "Conversations",
            ]
          : ["Conversations"],
    }),
    getConversationHistory: build.query<DisplayMessage[], number>({
      query: (id) => `AI/AI_GetConversationHistory/${id}`,
      providesTags: (result, error, id) => [
        { type: "ConversationHistory", id },
      ],
      transformResponse: (response: ChatMessage[]) => {
        const filteredResponse = response.filter(
          (message) => message.role !== "system"
        );
        return filteredResponse.map(
          (message): DisplayMessage => ({
            role: message.role as "user" | "assistant",
            content: message.content,
            date: "",
            time: "",
          })
        );
      },
    }),

    getTranscription: build.mutation<TAIResponse, { file: File }>({
      query: ({ file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `AI/TranscribeAudio`,
          method: "POST",
          body: formData,
        };
      },
    }),
    insertAllAnswersAI: build.mutation<
      any,
      {
        name: string;
        phone: string;
        questionAnswers: Array<{ questionText: string; answerText: string }>;
      }
    >({
      query: (body) => ({
        url: "Questions/InsertAllAnswersQuestionsAI",
        method: "POST",
        body,
      }),
    }),
    getAudioUrlsFromTexts: build.mutation<
      TQuestionAudioUrlResponseDto[],
      TQuestionAudioRequestDto[]
    >({
      query: (questions) => ({
        url: "AI/getAudioUrlsForQuestionnaire",
        method: "POST",
        body: questions,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetResponseFromAIMutation,
  useGetAudioUrlsFromTextsMutation,
  useGetConversationHistoryQuery,
  useInsertAllAnswersAIMutation,
  useGetTranscriptionMutation,
} = aiApi;
